import type { WidgetConfig } from '../store/widget.store';
import { widgetTemplate } from './templates';

type StockLevel = { inventoryQuantity: number; selectedOptions: { name: string; value: string }[] };

const host = import.meta.env.VITE_HOST;

declare namespace Shopify {
    const shop: string;
    const locale: string;
    const currency: { rate: number };
}

export enum WidgetType {
    DEFAULT = 'default',
    COUNTDOWN = '{{countdown}}',
    INVENTORY = '{{inventory}}',
    FREE_SHIPPING = '{{free_shipping}}',
}

export class Widget {
    private readonly widget: HTMLDivElement;
    private readonly titleElement: HTMLDivElement;
    private readonly finalizedContentElement: HTMLDivElement;
    private daysNodes: NodeListOf<HTMLDivElement> | null = null;
    private hoursNodes: NodeListOf<HTMLDivElement> | null = null;
    private minutesNodes: NodeListOf<HTMLDivElement> | null = null;
    private secondsNodes: NodeListOf<HTMLDivElement> | null = null;
    private variableNodes: NodeListOf<HTMLDivElement> | null = null;
    private readonly paddingHolder: HTMLDivElement;
    private timer = 0;
    private stockLevel: StockLevel[] = [];
    private countdownTimer: null | Timer = null;
    private cartAmount: { amount: number; currency: string } | null = null;
    private isFinalized: boolean = false;
    private _widgetType: WidgetType = WidgetType.DEFAULT;
    get widgetType(): WidgetType {
        return this._widgetType;
    }
    private returnOpacityTimeout: null | Timer = null;
    private scrollResizeObserver: ResizeObserver | null = null;
    private lastScrollContainerWidth = 0;

    private static readonly SCROLL_SPEED = 80; // px per second
    private static readonly SCROLL_GAP_WIDTH = 80; // must match .scroll-gap min-width in CSS
    constructor(
        private readonly config: Partial<WidgetConfig>,
        private readonly pageCode?: string
    ) {
        this.widget = document.createElement('div');
        this.widget.classList.add('mv-cd-widget');
        this.widget.innerHTML = widgetTemplate.replace(':ID:', String(config._id || 'unknown'));
        this.paddingHolder = this.widget.querySelector('.padding-holder') as HTMLDivElement;

        if (!this.paddingHolder) {
            throw new Error('[MV-CD] Padding holder element not found in widget template.');
        }

        this.titleElement = this.widget.querySelector('.mv-cd-title') as HTMLDivElement;
        this.finalizedContentElement = this.widget.querySelector('.mv-cd-finalized-content') as HTMLDivElement;

        this.setStyle(config as WidgetConfig & { maxWidth: string });
        this.setContent({ text: config.titleText, endText: config.endText, scrollingText: config.scrollingText });
        this.runAnimation();
        this.initHideOnHover(config as WidgetConfig);
    }

    destroy() {
        switch (this._widgetType) {
            case WidgetType.COUNTDOWN:
                this.destroyCountdownContent();
                break;
            case WidgetType.INVENTORY:
                this.destroyInventoryContent();
                break;
            case WidgetType.FREE_SHIPPING:
                this.destroyFreeShippingContent();
                break;
        }
        this.scrollResizeObserver?.disconnect();
        this.scrollResizeObserver = null;
        this.widget.remove();
    }

    setStyle(config: Partial<WidgetConfig> & { maxWidth?: string }) {
        const {
            verticalPadding,
            horizontalPadding,
            borderColor,
            backgroundColor,
            borderRadius,
            borderWidth,
            maxWidth,
            fullWidth,
            topMargin,
            bottomMargin,
            gradientEnabled,
            gradientColor,
            gradientDirection,
            shadowEnabled,
            shadowIntensity,
            animationStyle,
        } = config;

        const widgetContainer = this.widget;

        if (verticalPadding !== undefined) {
            this.paddingHolder.style.paddingTop = `${verticalPadding}px`;
            this.paddingHolder.style.paddingBottom = `${verticalPadding}px`;
        }
        if (horizontalPadding !== undefined) {
            this.paddingHolder.style.paddingLeft = `${horizontalPadding}px`;
            this.paddingHolder.style.paddingRight = `${horizontalPadding}px`;
        }
        if (topMargin !== undefined) {
            widgetContainer.style.marginTop = `${topMargin}px`;
        }
        if (bottomMargin !== undefined) {
            widgetContainer.style.marginBottom = `${bottomMargin}px`;
        }
        if (borderColor) {
            widgetContainer.style.borderColor = borderColor;
            widgetContainer.style.setProperty('--mv-border-color', borderColor);
        }
        if (backgroundColor) {
            if (gradientEnabled && gradientColor) {
                widgetContainer.style.background = `linear-gradient(${gradientDirection || 'to right'}, ${backgroundColor}, ${gradientColor})`;
            } else {
                widgetContainer.style.background = '';
                widgetContainer.style.backgroundColor = backgroundColor;
            }
        }
        if (borderRadius !== undefined) {
            widgetContainer.style.borderRadius = `${borderRadius}px`;
        }
        if (borderWidth !== undefined) {
            widgetContainer.style.borderWidth = `${borderWidth}px`;
        }
        if (fullWidth) {
            widgetContainer.style.width = '100%';
        } else {
            widgetContainer.style.width = 'auto';
        }

        if (maxWidth !== undefined) {
            widgetContainer.style.maxWidth = maxWidth;
            widgetContainer.style.width = '100%';
        }

        widgetContainer.style.borderStyle = 'solid';

        // Animation style — toggle CSS classes (must run before shadow so glow can suppress inline box-shadow)
        const animClasses = ['mv-cd-shimmer', 'mv-cd-glow', 'mv-cd-pulse-border', 'mv-cd-gradient-shift'];
        animClasses.forEach(c => widgetContainer.classList.remove(c));

        if (animationStyle && animationStyle !== 'none') {
            widgetContainer.classList.add(`mv-cd-${animationStyle}`);
        }

        // Set glow colour variable so the animation uses the widget's own background colour
        if (backgroundColor) {
            widgetContainer.style.setProperty('--mv-glow-color', backgroundColor);
        }

        // Shadow — skip inline box-shadow when glow animation owns it
        if (shadowEnabled !== undefined && animationStyle !== 'glow') {
            if (shadowEnabled) {
                const intensity = shadowIntensity || 'medium';
                const shadows: Record<string, string> = {
                    light: '0 2px 8px rgba(0,0,0,0.12)',
                    medium: '0 4px 20px rgba(0,0,0,0.25), 0 0 40px rgba(0,0,0,0.1)',
                    heavy: '0 8px 40px rgba(0,0,0,0.4), 0 0 60px rgba(0,0,0,0.2)',
                };
                widgetContainer.style.boxShadow = (shadows[intensity] ?? shadows.medium) as string;
            } else {
                widgetContainer.style.boxShadow = 'none';
            }
        } else if (animationStyle === 'glow') {
            widgetContainer.style.boxShadow = '';
        }
    }

    setContent({ text, endText, scrollingText = false }: { text?: string; endText?: string; scrollingText?: boolean }) {
        this.setContentType(text);

        if (text !== undefined) {
            if ([WidgetType.COUNTDOWN, WidgetType.INVENTORY, WidgetType.FREE_SHIPPING].includes(this._widgetType)) {
                this.titleElement.innerHTML = this.evaluateMacros(text, this._widgetType);
            } else {
                this.titleElement.innerHTML = text;
            }
        }

        if (endText !== undefined) {
            this.finalizedContentElement.innerHTML = endText;
        }

        const inner = this.widget.querySelector('.mv-cd-inner') as HTMLElement;
        const existingTrack = this.widget.querySelector('.scroll-track') as HTMLElement | null;

        // Always disconnect previous observer before rebuilding
        this.scrollResizeObserver?.disconnect();
        this.scrollResizeObserver = null;
        this.lastScrollContainerWidth = 0;

        if (scrollingText) {
            // Remove old track cleanly (also detaches this.titleElement if it was inside)
            if (existingTrack) {
                existingTrack.parentNode!.removeChild(existingTrack);
            }

            // Build minimal 1-copy track; ResizeObserver will fill it once in DOM
            const track = document.createElement('div');
            track.className = 'scroll-track';
            track.appendChild(this.titleElement);
            const gap = document.createElement('span');
            gap.className = 'scroll-gap';
            gap.setAttribute('aria-hidden', 'true');
            track.appendChild(gap);
            inner.appendChild(track);

            this.paddingHolder.classList.add('scroll-content');

            // ResizeObserver fires once the element has layout (even on first DOM insertion)
            this.scrollResizeObserver = new ResizeObserver(() => this.refillScrollTrack());
            this.scrollResizeObserver.observe(this.paddingHolder);
        } else {
            if (existingTrack) {
                inner.insertBefore(this.titleElement, existingTrack);
                existingTrack.parentNode!.removeChild(existingTrack);
            }
            this.paddingHolder.classList.remove('scroll-content');
            this.requeryLiveNodes();
        }
    }

    private requeryLiveNodes() {
        if (this._widgetType === WidgetType.COUNTDOWN) {
            this.daysNodes = this.widget.querySelectorAll('.mv-cd-days');
            this.hoursNodes = this.widget.querySelectorAll('.mv-cd-hours');
            this.minutesNodes = this.widget.querySelectorAll('.mv-cd-minutes');
            this.secondsNodes = this.widget.querySelectorAll('.mv-cd-seconds');
        } else if (this._widgetType === WidgetType.INVENTORY || this._widgetType === WidgetType.FREE_SHIPPING) {
            this.variableNodes = this.widget.querySelectorAll('.mv-cd-variable');
        }
    }

    private refillScrollTrack() {
        const track = this.widget.querySelector('.scroll-track') as HTMLElement | null;
        if (!track || !track.contains(this.titleElement)) return;

        const containerWidth = this.paddingHolder.offsetWidth;
        if (containerWidth === 0) return;
        // Skip if width unchanged (avoids thrashing when the rebuild itself triggers a resize)
        if (containerWidth === this.lastScrollContainerWidth) return;
        this.lastScrollContainerWidth = containerWidth;

        const titleWidth = this.titleElement.offsetWidth;
        if (titleWidth === 0) return;

        const unitWidth = titleWidth + Widget.SCROLL_GAP_WIDTH;
        const copies = Math.max(2, Math.ceil(containerWidth / unitWidth) + 1);

        // Rebuild track with the right number of copies
        while (track.firstChild) track.removeChild(track.firstChild);
        for (let i = 0; i < copies; i++) {
            const el = i === 0 ? this.titleElement : (this.titleElement.cloneNode(true) as HTMLElement);
            if (i > 0) el.setAttribute('aria-hidden', 'true');
            track.appendChild(el);
            const gap = document.createElement('span');
            gap.className = 'scroll-gap';
            gap.setAttribute('aria-hidden', 'true');
            track.appendChild(gap);
        }

        // Animate exactly one unit so the loop is seamless with any number of copies
        const duration = unitWidth / Widget.SCROLL_SPEED;
        track.style.setProperty('--scroll-unit', `${unitWidth}px`);
        track.style.setProperty('--scroll-duration', `${duration}s`);

        this.requeryLiveNodes();
    }

    private destroyCountdownContent() {
        this.daysNodes = null;
        this.hoursNodes = null;
        this.minutesNodes = null;
        this.secondsNodes = null;
        if (this.countdownTimer) {
            clearTimeout(this.countdownTimer);
        }
        this.countdownTimer = null;
    }

    private destroyInventoryContent() {
        this.variableNodes = null;
    }

    private destroyFreeShippingContent() {
        this.variableNodes = null;
    }

    private setContentType(content?: string) {
        let detectedType: WidgetType = WidgetType.DEFAULT;

        if (content) {
            const matches = [...content.matchAll(/{{.+?}}/g)];

            if (matches.length > 0) {
                for (let i = matches.length - 1; i >= 0; i--) {
                    const match = matches?.[i]?.[0]?.trim() || '';

                    if (
                        [WidgetType.COUNTDOWN, WidgetType.INVENTORY, WidgetType.FREE_SHIPPING].includes(
                            match as WidgetType
                        )
                    ) {
                        detectedType = match as WidgetType;
                        break;
                    }
                }
            }
        }

        if (this._widgetType !== detectedType) {
            if (this._widgetType === WidgetType.COUNTDOWN) {
                clearTimeout(this.countdownTimer!);
            }
            this._widgetType = detectedType;
        }
    }

    private runAnimation() {
        if (this._widgetType === WidgetType.COUNTDOWN) {
            this.timer = this.getSecondsToCountdown();
            this.startCountdown();
        } else if (this._widgetType === WidgetType.INVENTORY && this.isStoreFront()) {
            this.initStockTracking();
        } else if (this._widgetType === WidgetType.FREE_SHIPPING && this.isStoreFront()) {
            this.initFreeShippingTracking();
        }
    }

    getWidgetElement(): HTMLDivElement {
        return this.widget;
    }

    private evaluateMacros(input: string, placeholder: WidgetType): string {
        const macrosRegex = new RegExp(placeholder, 'g');
        let htmlReplacement = '';

        if (placeholder === WidgetType.COUNTDOWN) {
            htmlReplacement = `<span class="mv-cd-timer">
                <span class="mv-cd-days mv-cd-countdown">00</span>
                <span class="mv-cd-semicolon" aria-hidden="true">:</span>
                <span class="mv-cd-hours mv-cd-countdown">00</span>
                <span class="mv-cd-semicolon" aria-hidden="true">:</span>
                <span class="mv-cd-minutes mv-cd-countdown">00</span>
                <span class="mv-cd-semicolon" aria-hidden="true">:</span>
                <span class="mv-cd-seconds mv-cd-countdown">00</span>
            </span>`;
        } else if (placeholder === WidgetType.FREE_SHIPPING) {
            htmlReplacement = `<span class="mv-cd-variable" style="vertical-align: middle;">${this.isStoreFront() ? '...' : '$100'}</span>`;
        } else if (placeholder === WidgetType.INVENTORY) {
            htmlReplacement = `<span class="mv-cd-variable" style="vertical-align: middle;">${this.isStoreFront() ? '...' : '10'}</span>`;
        }

        const replacedInput = input.replaceAll(macrosRegex, htmlReplacement);
        return replacedInput;
    }

    private getSecondsToCountdown(): number {
        const { fixedDate = '', fixedMinutes = 0, resumeSessionTimer = false, timerType } = this.config;

        if (timerType === 'fixed_date') {
            const end = new Date(fixedDate).getTime();
            const now = Date.now();
            const diff = end - now;
            return Math.max(Math.floor(diff / 1000), 0);
        }
        if (timerType === 'fixed_minutes') {
            return resumeSessionTimer && globalThis.sessionStorage.getItem(this.timerKey)
                ? (new Date(globalThis.sessionStorage.getItem(this.timerKey) as string).getTime() - Date.now()) / 1000
                : fixedMinutes * 60;
        }
        console.error(`[MV-CD] Unknown timer type: ${timerType}`);
        return 0;
    }

    get timerKey(): string {
        const { _id } = this.config;
        return `mv-cd-timer-${_id}`;
    }

    private startCountdown() {
        const { resumeSessionTimer } = this.config;
        const secondsToCountdown = this.timer;
        const seconds = Math.floor(secondsToCountdown % 60);
        const minutes = Math.floor(secondsToCountdown / 60) % 60;
        const hours = Math.floor((secondsToCountdown / 60 / 60) % 24);
        const days = Math.floor(secondsToCountdown / 60 / 60 / 24);

        this.daysNodes!.forEach(node => {
            node.textContent = String(days).padStart(2, '0');
        });

        this.hoursNodes!.forEach(node => {
            node.textContent = String(hours).padStart(2, '0');
        });

        this.minutesNodes!.forEach(node => {
            node.textContent = String(minutes).padStart(2, '0');
        });

        this.secondsNodes!.forEach(node => {
            node.textContent = String(seconds).padStart(2, '0');
        });

        if (secondsToCountdown <= 60) {
            this.widget.classList.add('mv-cd-urgent');
        } else {
            this.widget.classList.remove('mv-cd-urgent');
        }

        if (resumeSessionTimer) {
            globalThis.sessionStorage.setItem(this.timerKey, new Date(Date.now() + this.timer * 1000).toISOString());
        } else {
            globalThis.sessionStorage.removeItem(this.timerKey);
        }

        if (this.timer > 0) {
            this.timer--;
            this.countdownTimer = setTimeout(() => this.startCountdown(), 1000);
        } else {
            this.finishWidget();
        }
    }

    private finishWidget() {
        const { endText = '', onFinish = 'do_nothing' } = this.config;
        this.isFinalized = true;

        if (onFinish === 'hide') {
            this.widget.style.display = 'none';
        } else if (onFinish === 'show_text') {
            this.widget.classList.add('mv-cd-finalized');
            const finalizedContent = this.widget.querySelector('.mv-cd-finalized-content') as HTMLDivElement;

            if (finalizedContent) {
                finalizedContent.innerHTML = endText;
            }
        }
    }

    private unFinalizeWidget() {
        if (this.isFinalized) {
            this.isFinalized = false;
            this.widget.classList.remove('mv-cd-finalized');
            this.widget.style.display = 'block';
        }
    }

    private getProductHandler(): string {
        if (globalThis.location.pathname.includes('/products/')) {
            const handler = globalThis.location.pathname.split('/').pop();

            if (!handler) {
                throw new Error('[MV-CD] Product ID not found in URL');
            }

            return handler;
        }
        throw new Error('[MV-CD] Product ID not found in URL');
    }

    private getShop(): string {
        const shop = (globalThis as any).Shopify?.shop;

        if (!shop) {
            throw new Error('[MV-CD] Shopify shop not found');
        }

        return shop;
    }

    private initStockTracking() {
        const handler = this.getProductHandler();
        const shop = this.getShop();
        const evtSource = new EventSource(`${host}/api/storefront/shop/product/stock?shop=${shop}&handler=${handler}`);
        evtSource.onmessage = event => {
            const data = JSON.parse(event.data) as { stockLevel: StockLevel[] };
            if (data.stockLevel) {
                this.stockLevel = data.stockLevel;
                this.renderStockLevel();
            }

            console.debug('[MV-CD] SSE message received:', data);
        };

        evtSource.addEventListener('error', err => {
            evtSource.close();
            setTimeout(() => {
                this.initStockTracking();
            }, 5000);
        });

        evtSource.addEventListener('open', () => {
            console.debug('[MV-CD] SSE connection opened.');
        });

        evtSource.addEventListener('close', () => {
            console.debug('[MV-CD] SSE connection closed.');
            setTimeout(() => {
                this.initStockTracking();
            }, 5000);
        });

        globalThis.document.querySelectorAll('fieldset input').forEach(option => {
            option.addEventListener('change', () => {
                this.renderStockLevel();
            });
        });
    }

    private renderStockLevel() {
        const options = globalThis.document.querySelectorAll('fieldset input');
        const selectedOptions: { name: string; value: string }[] = [];

        if (options.length > 0) {
            options.forEach(option => {
                if ((option as HTMLInputElement).checked) {
                    const optionName = option.getAttribute('name');
                    const optionValue = (option as HTMLInputElement).value;
                    if (optionName) {
                        selectedOptions.push({ name: optionName, value: optionValue });
                    }
                }
            });
        } else {
            selectedOptions.push({ name: 'Title', value: 'Default Title' });
        }

        if (selectedOptions.length === 0) {
            console.warn('[MV-CD] No selected options found.');
            return;
        }

        const selectedStock = this.stockLevel.find(stock =>
            stock.selectedOptions.every(selOpt =>
                selectedOptions.some(sel => sel.name === selOpt.name && sel.value === selOpt.value)
            )
        );

        if (!selectedStock) {
            console.warn('[MV-CD] No stock level found for the selected options.');
            return;
        }

        const inventoryCount = selectedStock.inventoryQuantity;

        this.variableNodes?.forEach(node => {
            node.textContent = String(inventoryCount);
        });

        if (inventoryCount <= 0) {
            this.finishWidget();
        } else {
            this.unFinalizeWidget();
        }
    }

    private initFreeShippingTracking() {
        const fetchCart = () => {
            fetch(`/cart.json`)
                .then(res => res.json())
                .then((cart: { total_price: number; currency: string }) => {
                    console.debug('cart', cart);
                    this.cartAmount = { amount: cart.total_price / 100, currency: cart.currency };
                    this.renderFreeShipping();
                });
        };

        document.addEventListener('cart:updated', fetchCart);

        if (
            // @ts-ignore
            globalThis.publish !== undefined &&
            // @ts-ignore
            globalThis.subscribe !== undefined &&
            // @ts-ignore
            PUB_SUB_EVENTS?.cartUpdate !== undefined
        ) {
            // @ts-ignore
            globalThis.subscribe(PUB_SUB_EVENTS.cartUpdate, fetchCart);
            console.debug('[MV-CD]: Subscribed to cart updates via pub/sub.');
        } else {
            console.debug('[MV-CD] Pub/Sub not available for cart updates.');
        }

        fetchCart();
    }

    private renderFreeShipping() {
        const { freeShippingThreshold = 0 } = this.config;
        const amountToAdd = Math.max(
            0,
            Shopify.currency.rate * freeShippingThreshold - (this.cartAmount ? this.cartAmount.amount : 0)
        );

        this.variableNodes?.forEach(node => {
            node.textContent = this.formatMoney(amountToAdd);
        });

        if (amountToAdd === 0) {
            this.finishWidget();
        } else {
            this.unFinalizeWidget();
        }
    }

    private formatMoney(amount: number, currency = 'USD') {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
        }).format(amount);
    }

    private isStoreFront(): boolean {
        return Boolean((globalThis as any).Shopify);
    }

    private initHideOnHover(config: WidgetConfig) {
        if (!this.isStoreFront()) {
            return;
        }
        const placement = config.placements?.find(p => p.page === this.pageCode);

        if (!placement) {
            console.debug(`[MVC] Cannot initialize hide on hover for page ${this.pageCode}.`);
            return;
        }

        if (placement?.hide_on_hover) {
            this.widget.addEventListener('pointerenter', () => {
                if (this.returnOpacityTimeout) {
                    clearTimeout(this.returnOpacityTimeout);
                }
                this.widget.classList.add('mv-cd-hovered');
                setTimeout(() => {
                    this.widget.style.display = 'none';
                }, 500);
                setTimeout(() => {
                    this.widget.style.display = 'block';
                    this.returnOpacityTimeout = setTimeout(() => {
                        this.widget.classList.remove('mv-cd-hovered');
                    }, 1_000);
                }, 30_000);
            });
        }
    }
}
